import { forwardRef, Ref, useState } from 'react';
import { useIntl } from 'react-intl';
import { User } from '@rossum/api-client/users';
import { VirtualizedListboxFixedSize } from '@rossum/components/virtualized-listbox/VirtualizedListboxFixedSize';
import {
  Autocomplete,
  CircularProgress,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
} from '@rossum/ui/material';
import { Url } from '../../../../../types/basic';

type TokenOwnerSelectProps = {
  allOrgUsers: User[];
  users: User[];
  value: Url | null;
  onChange: (values: Url | null) => void;
  loading?: boolean;
};

const checkSearchValidity = (search: string, option: string) =>
  option.toLowerCase().includes(search.toLowerCase());

const getUserOptionParams = ({
  email,
  username,
  firstName,
  lastName,
}: User) => ({
  primaryText: email || username,
  secondaryText: lastName && firstName ? `${firstName} ${lastName}` : undefined,
});

export const TokenOwnerSelect = forwardRef(
  (
    {
      allOrgUsers,
      loading = false,
      value,
      onChange,
      users,
    }: TokenOwnerSelectProps,
    ref: Ref<unknown>
  ) => {
    const [inputValue, setInputValue] = useState<string>('');

    const intl = useIntl();
    const selectedUserObject = allOrgUsers.find(({ url }) => url === value);

    return (
      <Autocomplete
        ref={ref}
        data-cy="extensions-users-select"
        slots={{
          paper: Paper,
          listbox: VirtualizedListboxFixedSize,
        }}
        slotProps={{
          paper: { elevation: 6 },
          listbox: {
            // @ts-expect-error
            itemSize: 55,
          },
        }}
        size="small"
        options={users}
        getOptionLabel={user => user.username}
        value={selectedUserObject ?? null}
        onChange={(_e, newValue) => onChange(newValue?.url ?? null)}
        disabled={loading}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={intl.formatMessage({
              id: 'containers.settings.extensions.createExtension.tokenOwner.placeholder',
            })}
            slotProps={{
              inputLabel: { shrink: true, ...params.InputLabelProps },
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        inputValue={inputValue}
        onInputChange={(_e, newInputValue) => {
          setInputValue(newInputValue);
        }}
        filterOptions={(options, state) => {
          return options.filter(option => {
            const { primaryText, secondaryText } = getUserOptionParams(option);
            return (
              checkSearchValidity(state.inputValue, primaryText) ||
              (secondaryText &&
                checkSearchValidity(state.inputValue, secondaryText))
            );
          });
        }}
        renderOption={(params, user) => {
          const { primaryText, secondaryText } = getUserOptionParams(user);
          return (
            <ListItemButton component="li" {...params} key={user.url} dense>
              <ListItemText primary={primaryText} secondary={secondaryText} />
            </ListItemButton>
          );
        }}
      />
    );
  }
);
